/**
 * The books, seen from inventory (T015, FR-129, research.md R9).
 *
 * The mirror of `src/modules/books/ui/stock.ts`, and for the same reason:
 * inventory does not import the books' code, it calls the books' HTTP API. Turn
 * either module off and the other keeps working; delete either and the other
 * still builds.
 *
 * This is the direction that matters for whoever unpacks a delivery — they are
 * standing in inventory, and the money should not need a second trip to another
 * screen.
 */

export type PlaceId = "drawer" | "cash_held" | "ewallet" | "capital_fund";

/** The places a delivery can be paid from. The drawer is not among them: */
/* drawer money leaves through the till's own Cash out, against an open shift. */
export const PAYABLE_FROM: Array<{ id: PlaceId; name: string }> = [
  { id: "ewallet", name: "E-wallet" },
  { id: "cash_held", name: "Cash held" },
  { id: "capital_fund", name: "Capital fund" },
];

export interface SpendingCategory {
  id: string;
  name: string;
  kind: "setup" | "goods" | "running" | "deposit";
}

/**
 * The books are not available to this person right now — switched off, not
 * permitted, or offline. Never shown as an error: the Delivery form simply
 * does not offer to record the spending, and the delivery still goes in.
 */
export class BooksUnavailable extends Error {
  constructor(
    readonly why: "off" | "not-allowed" | "offline",
    message: string,
  ) {
    super(message);
    this.name = "BooksUnavailable";
  }
}

export interface NewSpending {
  opId: string;
  spentOn: string;
  supplier: string | null;
  description: string;
  categoryId: string;
  amount: number;
  parts: Array<{ place: PlaceId; amount: number }>;
  /** The stock movements this purchase wrote, sharing its `opId`. */
  inventoryOpId: string;
}

export function booksApi(token: string) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`/api/books${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body === undefined ? {} : { "content-type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new BooksUnavailable("offline", "No connection to the books.");
    }

    if (!response.ok) {
      // A disabled module answers 404 — it does not exist, rather than refusing.
      if (response.status === 404) throw new BooksUnavailable("off", "The books are switched off.");
      if (response.status === 403) throw new BooksUnavailable("not-allowed", "Not allowed to record spending.");

      let message = `That did not go through (${response.status}).`;
      try {
        const parsed = (await response.json()) as { error?: { message?: string } };
        message = parsed.error?.message ?? message;
      } catch {
        // Not JSON — keep the generic sentence.
      }
      throw new Error(message);
    }

    return (await response.json()) as T;
  }

  return {
    /** Goods categories first: a delivery is nearly always one of them. */
    categories: () => request<{ categories: SpendingCategory[] }>("GET", "/categories"),

    /**
     * Carries the delivery's own `opId`, so pressing Save twice — or retrying
     * after the stock went in and the money did not — records one expense.
     */
    createExpense: (body: NewSpending) =>
      request<{ duplicate: boolean }>("POST", "/expenses", body),
  };
}

export type BooksSpendingApi = ReturnType<typeof booksApi>;
